import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NgControl,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { VectronicValidators } from '@vectronic/util';
import { TouchedErrorStateMatcher } from '../../utils/touched-error-state-matcher';

export interface UserRolesValue {
  owner: string;
  deviceManagers: string[];
  dataManagers: string[];
}

const DUPLICATE_EMAIL_ERROR = 'duplicateEmail';

function normalizeEmail(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

@Component({
  selector: 'vectronic-formly-user-roles',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './user-roles.component.html',
  styleUrls: ['./user-roles.component.scss'],
})
export class UserRolesComponent implements ControlValueAccessor, OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly ngControl = inject(NgControl, { self: true, optional: true });
  readonly matcher = new TouchedErrorStateMatcher();

  readonly id = input<string | undefined>(undefined);
  readonly maxDeviceManagers = input<number>(Infinity);
  readonly maxDataManagers = input<number>(Infinity);

  protected readonly disabled = signal(false);

  private readonly duplicateEmailValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = normalizeEmail(control.value);
    if (!email) return null;

    const controls = this.allEmailControls;
    const index = controls.indexOf(control);
    const previousControls = index === -1 ? controls : controls.slice(0, index);

    return previousControls.some((c) => normalizeEmail(c.value) === email)
      ? { [DUPLICATE_EMAIL_ERROR]: true }
      : null;
  };

  readonly internalForm = new FormGroup({
    owner: new FormControl('', [Validators.required, VectronicValidators.email]),
    deviceManagers: new FormArray<FormControl>([]),
    dataManagers: new FormArray<FormControl>([]),
  });

  private onChange: (value: UserRolesValue) => void;
  private onTouched: () => void;

  constructor() {
    this.ownerControl.addValidators(this.duplicateEmailValidator);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.internalForm.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.revalidateDuplicates();
      this.onChange?.({
        owner: this.ownerControl.value ?? '',
        deviceManagers: this.deviceManagersArray.value ?? [],
        dataManagers: this.dataManagersArray.value ?? [],
      });
      this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    });

    this.ngControl?.control?.events
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => {
        if (event instanceof TouchedChangeEvent && event.touched) {
          this.internalForm.markAllAsTouched();
        }
      });

    this.ngControl?.control?.addValidators(this.internalFormValidator);
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }

  get ownerControl(): FormControl {
    return this.internalForm.get('owner') as FormControl;
  }

  get deviceManagersArray(): FormArray<FormControl> {
    return this.internalForm.get('deviceManagers') as FormArray<FormControl>;
  }

  get dataManagersArray(): FormArray<FormControl> {
    return this.internalForm.get('dataManagers') as FormArray<FormControl>;
  }

  private get allEmailControls(): AbstractControl[] {
    return [
      this.ownerControl,
      ...this.deviceManagersArray.controls,
      ...this.dataManagersArray.controls,
    ];
  }

  addDeviceManager(): void {
    const ctrl = this.createEmailControl();
    if (this.ngControl?.control?.touched) ctrl.markAsTouched();
    this.deviceManagersArray.push(ctrl);
  }

  removeDeviceManager(i: number): void {
    this.deviceManagersArray.removeAt(i);
  }

  addDataManager(): void {
    const ctrl = this.createEmailControl();
    if (this.ngControl?.control?.touched) ctrl.markAsTouched();
    this.dataManagersArray.push(ctrl);
  }

  removeDataManager(i: number): void {
    this.dataManagersArray.removeAt(i);
  }

  writeValue(value: UserRolesValue): void {
    const initial = value ?? { owner: '', deviceManagers: [], dataManagers: [] };

    this.internalForm.patchValue({ owner: initial.owner ?? '' }, { emitEvent: false });

    this.deviceManagersArray.clear({ emitEvent: false });
    (initial.deviceManagers ?? []).forEach((email) =>
      this.deviceManagersArray.push(this.createEmailControl(email), { emitEvent: false }),
    );

    this.dataManagersArray.clear({ emitEvent: false });
    (initial.dataManagers ?? []).forEach((email) =>
      this.dataManagersArray.push(this.createEmailControl(email), { emitEvent: false }),
    );

    if (this.disabled()) {
      this.internalForm.disable({ emitEvent: false });
    }

    this.revalidateDuplicates();
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }

  registerOnChange(fn: (value: UserRolesValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internalForm.disable({ emitEvent: false });
    } else {
      this.internalForm.enable({ emitEvent: false });
    }
    this.disabled.set(isDisabled);
  }

  private revalidateDuplicates(): void {
    this.allEmailControls.forEach((c) =>
      c.updateValueAndValidity({ emitEvent: false, onlySelf: true }),
    );
    this.internalForm.updateValueAndValidity({ emitEvent: false });
  }

  private createEmailControl(value = ''): FormControl {
    return new FormControl(value, [
      Validators.required,
      VectronicValidators.email,
      this.duplicateEmailValidator,
    ]);
  }

  private internalFormValidator: ValidatorFn = (): ValidationErrors | null => {
    return this.internalForm?.valid ? null : { invalidUserRoles: true };
  };
}
